import React from "react";
import {
  select,
  selectAll,
  bisectLeft,
  axisBottom,
  axisLeft,
  scaleBand,
  scaleLinear,
  scaleLog,
} from "d3";
import PropTypes from "prop-types";
import {
  pathogenicStrongColor,
  uncertainColor,
  benignStrongColor,
} from "../../../../common/helpers/colors";
import { areEqual } from "../../../../common/helpers/helpers";

const COLORS = {
  benign: benignStrongColor,
  uncertain: uncertainColor,
  pathogenic: pathogenicStrongColor,
};
class GeneHistogram extends React.Component {
  constructor() {
    super();
    this.componentName = "GeneHistogram";
    this.state = { width: 600 };
  }

  componentDidMount() {
    this.setState({ width: Math.max(this.container.offsetWidth, 980) });
  }

  componentDidUpdate() {
    const { data, uniqueKey, cutOffFreq } = this.props;

    if (data) {
      selectAll(`#gene_histogram_${uniqueKey} > svg > *`).remove();

      // Creates the initial space for the chart
      const svg = select(`#gene_histogram_${uniqueKey} > svg`);
      const margin = { top: 20, right: 20, bottom: 150, left: 80 };
      const width = +svg.attr("width") - margin.left - margin.right;
      const height = +svg.attr("height") - margin.top - margin.bottom;

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Draw x-axis
      const x = scaleBand().rangeRound([0, width]).padding(0.1);
      x.domain(
        // eslint-disable-next-line react/prop-types
        data.map((d) => {
          return d.x_axis;
        })
      );
      g.append("g")
        .attr("class", "axis axis--x")
        .attr("transform", `translate(0,${height})`)
        .call(
          axisBottom(x).tickFormat((d, i) => {
            let xAxisLabel;
            if (i === 0) {
              xAxisLabel = "no frequency";
            } else {
              xAxisLabel = `<${d}`;
            }
            return xAxisLabel;
          })
        )
        .selectAll("text")
        .attr("class", "varsome-axis-label")
        .style("text-anchor", "end")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-45)");

      // Draw y-axis.
      // Get max value for y axis.
      let maxy = 0;
      // eslint-disable-next-line react/prop-types
      for (let i = data.length - 1; i >= 0; i--) {
        const max = Math.max(
          data[i].benign,
          data[i].pathogenic,
          data[i].uncertain
        );
        const val = parseFloat(max);
        if (val > maxy) {
          maxy = val;
        }
      }

      // Calculate the left limit of y-axis based on the max value.
      let yLeftLimit;
      if (maxy <= 10) {
        yLeftLimit = 10;
      } else if (maxy <= 100) {
        yLeftLimit = 100;
      } else if (maxy <= 1000) {
        yLeftLimit = 1000;
      } else if (maxy > 1000 && maxy <= 10000) {
        yLeftLimit = 10000;
      } else {
        yLeftLimit = 1000000;
      }

      // Interpolate an output value.
      const interpolator = (a, b) => {
        const y0 = a;
        const y1 = b;
        const k = 0.1 / yLeftLimit;

        const positive = scaleLog()
          .domain([k, 1])
          .range([y0 + y1, y1]);

        return (t) => {
          if (t > k) {
            return positive(t);
          }
          return y0 + y1;
        };
      };

      // Create a scale using it.
      const y = scaleLinear()
        .range([height, 0])
        .domain([0, yLeftLimit])
        .interpolate(interpolator);

      // Set the ticks.
      const ticks = [0.1];
      y.domain().forEach((d) => {
        let o = d;
        while (o > 0.1) {
          ticks.push(o);
          o /= 10;
        }
      });

      g.append("g")
        .call(
          axisLeft()
            .scale(y)
            .tickValues(ticks)
            .tickFormat((d) => {
              const array = ["", "k", "M", "G", "T", "P"];
              let i = 0;
              let u = d;
              while (u >= 1000) {
                i += 1;
                u /= 1000;
              }
              u = `${u} ${array[i]}`;
              return u;
            })
        )
        .append("text")
        .attr("class", "varsome-axis-label")
        .attr("y", 6)
        .attr("dy", "0.71em")
        .attr("text-anchor", "end")
        .text("value");

      g.append("text")
        .attr("text-anchor", "middle") // this makes it easy to centre the text as the transform is applied to the anchor
        .attr("transform", `translate(-40,${height / 2})rotate(-90)`) // text is drawn off the screen top left, move down and out and rotate
        .text("Variant count");

      g.append("text")
        .attr("text-anchor", "middle") // this makes it easy to centre the text as the transform is applied to the anchor
        .attr("transform", `translate(${width / 2},${height + 105})`) // centre below axis
        .text("Frequency");

      // For each value in our dataset, places and styles a bar on the chart.

      const rect = g.selectAll("rect").data(data).enter();

      // Draw pathogenic rectangle
      rect
        .append("rect")
        .attr("x", (d) => {
          return x(d.x_axis) + x.bandwidth() / 8;
        })
        .attr("y", (d) => {
          if (d.pathogenic !== 0) return y(d.pathogenic);
          return null;
        })
        .attr("width", x.bandwidth() / 4 - 2)
        .style("fill", COLORS.pathogenic)
        .attr("height", (d) => {
          if (d.pathogenic !== 0) {
            return height - y(d.pathogenic);
          }
          return null;
        })
        .append("title")
        .text((d) => {
          const benignF = d.benign
            ? (
                (100 * d.benign) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const pathogenicF = d.pathogenic
            ? (
                (100 * d.pathogenic) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const uncertainF = d.uncertain
            ? (
                (100 * d.uncertain) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const text = `${pathogenicF}% pathogenic, ${uncertainF}% uncertain, ${benignF}% benign.`;
          return text;
        });

      // Draw pathogenic text
      rect
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", (d) => {
          return x(d.x_axis) + x.bandwidth() / 8 + (x.bandwidth() / 4 - 2) / 2;
        })
        .attr("y", (d) => {
          if (d.pathogenic !== 0) return y(d.pathogenic) - 5;
          return null;
        })
        .style("fill", COLORS.pathogenic)
        .attr("font-size", "9px")
        .text((d) => {
          if (d.pathogenic !== 0) return d.pathogenic;
          return null;
        });

      // Draw uncertain
      rect
        .append("rect")
        .attr("x", (d) => {
          return x(d.x_axis) + (3 * x.bandwidth()) / 8;
        })
        .attr("y", (d) => {
          if (d.uncertain !== 0) return y(d.uncertain);
          return null;
        })
        .attr("width", x.bandwidth() / 4 - 2)
        .style("fill", COLORS.uncertain)
        .attr("height", (d) => {
          if (d.uncertain !== 0) {
            return height - y(d.uncertain);
          }
          return null;
        })
        .append("title")
        .text((d) => {
          const benignF = d.benign
            ? (
                (100 * d.benign) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const pathogenicF = d.pathogenic
            ? (
                (100 * d.pathogenic) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const uncertainF = d.uncertain
            ? (
                (100 * d.uncertain) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const text = `${pathogenicF}% pathogenic, ${uncertainF}% uncertain, ${benignF}% benign.`;
          return text;
        });

      rect
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", (d) => {
          return (
            x(d.x_axis) + (3 * x.bandwidth()) / 8 + (x.bandwidth() / 4 - 2) / 2
          );
        })
        .attr("y", (d) => {
          if (d.uncertain !== 0) return y(d.uncertain) - 5;
          return null;
        })
        .style("fill", COLORS.uncertain)
        .attr("font-size", "9px")
        .text((d) => {
          if (d.uncertain !== 0) return d.uncertain;
          return null;
        });

      // Draw benign
      rect
        .append("rect")
        .attr("x", (d) => {
          return x(d.x_axis) + (5 * x.bandwidth()) / 8;
        })
        .attr("y", (d) => {
          if (d.benign !== 0) return y(d.benign);
          return null;
        })
        .attr("width", x.bandwidth() / 4 - 2)
        .style("fill", COLORS.benign)
        .attr("height", (d) => {
          if (d.benign !== 0) {
            return height - y(d.benign);
          }
          return null;
        })
        .append("title")
        .text((d) => {
          const benignF = d.benign
            ? (
                (100 * d.benign) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const pathogenicF = d.pathogenic
            ? (
                (100 * d.pathogenic) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const uncertainF = d.uncertain
            ? (
                (100 * d.uncertain) /
                (d.uncertain + d.benign + d.pathogenic)
              ).toFixed(1)
            : 0;
          const text = `${pathogenicF}% pathogenic, ${uncertainF}% uncertain, ${benignF}% benign.`;
          return text;
        });

      rect
        .append("text")
        .attr("text-anchor", "middle")
        .attr("x", (d) => {
          return (
            x(d.x_axis) + (5 * x.bandwidth()) / 8 + (x.bandwidth() / 4 - 2) / 2
          );
        })
        .attr("y", (d) => {
          if (d.benign !== 0) return y(d.benign) - 5;
          return null;
        })
        .style("fill", COLORS.benign)
        .attr("font-size", "9px")
        .text((d) => {
          if (d.benign !== 0) return d.benign;
          return null;
        });

      g.append("rect")
        .attr("transform", `translate(${0},${height + 70})`)
        .attr("width", 10)
        .attr("height", 10)
        .style("fill", COLORS.pathogenic);

      g.append("text")
        .text("pathogenic")
        .attr("transform", `translate(${15},${height + 80})`);

      g.append("rect")
        .attr("transform", `translate(${115},${height + 70})`)
        .attr("width", 10)
        .attr("height", 10)
        .style("fill", COLORS.uncertain);

      g.append("text")
        .text("uncertain")
        .attr("transform", `translate(${130},${height + 80})`);

      g.append("rect")
        .attr("transform", `translate(${230},${height + 70})`)
        .attr("width", 10)
        .attr("height", 10)
        .style("fill", COLORS.benign);

      g.append("text")
        .text("benign")
        .attr("transform", `translate(${245},${height + 80})`);

      // Draw the cut-off frequency
      if (cutOffFreq) {
        const lineIndex = bisectLeft(x.domain(), cutOffFreq);
        rect
          .append("line")
          .attr(
            "x1",
            x(x.domain()[lineIndex]) - x.step() / 2 + x.bandwidth() / 2
          )
          .attr(
            "x2",
            x(x.domain()[lineIndex]) - x.step() / 2 + x.bandwidth() / 2
          )
          .attr("y1", 0)
          .attr("y2", height)
          .style("stroke", COLORS.uncertain)
          .style("stroke-width", 1)
          .style("stroke-dasharray", "2,2");

        g.append("line")
          .attr("x1", 0)
          .attr("x2", 10)
          .attr("y1", height + 95)
          .attr("y2", height + 95)
          .style("stroke", COLORS.uncertain)
          .style("stroke-width", 1)
          .style("stroke-dasharray", "2,2");

        g.append("text")
          .text("benign cut-off frequency")
          .attr("transform", `translate(${15},${height + 100})`);
      }
    }
  }

  render() {
    const { data, uniqueKey } = this.props;
    const { width } = this.state;

    return (
      <div
        className="tw-w-full tw-overflow-x-auto"
        ref={(ref) => {
          this.container = ref;
        }}
        id={`gene_histogram_${uniqueKey}`}
      >
        {data ? <svg width={width} height="500" /> : null}
      </div>
    );
  }
}

GeneHistogram.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      benign: PropTypes.number,
      from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      pathogenic: PropTypes.number,
      to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      uncertain: PropTypes.number,
      x_axis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  cutOffFreq: PropTypes.number,
  uniqueKey: PropTypes.string,
};

GeneHistogram.defaultProps = {
  data: null,
  cutOffFreq: 0,
  uniqueKey: null,
};

export default React.memo(GeneHistogram, areEqual);
